import React from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  FormGroup,
  Label,
  Input
} from "reactstrap";
import { DIAS_SEMANA } from "../horariosClubesComponentConstants";
import {
  Horario,
  Mantenimiento,
  Club,
  LoadingStates
} from "../horariosClubesComponentTypes";

interface Props {
  isOpen: boolean;
  toggle: () => void;
  mantenimientos: Mantenimiento[];
  clubsDisponibles: Club[];
  mantenimientosSeleccionados: string[];
  clubesSeleccionados: string[];
  nuevaConfiguracion: {
    mantenimientoIds: number[];
    clubIds: number[];
    horarios: Horario[];
  };
  onMantenimientoChange: (mantenimientoIds: string[]) => void;
  onClubChange: (clubIds: string[]) => void;
  onHorarioChange: (horarios: Horario[]) => void;
  onSeleccionarTodosMantenimientos: () => void;
  onCrear: () => void;
  onSeleccionarTodosClubes: () => void;
  loadingStates: LoadingStates;
  isAnyOperationInProgress: () => boolean;
}

const ModalNuevaConfiguracion: React.FC<Props> = ({
  isOpen,
  toggle,
  mantenimientos,
  clubsDisponibles,
  mantenimientosSeleccionados,
  clubesSeleccionados,
  nuevaConfiguracion,
  onMantenimientoChange,
  onClubChange,
  onHorarioChange,
  onSeleccionarTodosMantenimientos: onSeleccionarTodosMantenimientos,
  onCrear,
  onSeleccionarTodosClubes: onSeleccionarTodosClubes,
  loadingStates,
  isAnyOperationInProgress
}) => {
  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Nueva Configuración de Horarios</ModalHeader>
      <ModalBody>
        <Row>
          <Col md={6}>
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Label>Selección de mantenimientos</Label>
                <Button
                  color="danger"
                  size="sm"
                  onClick={onSeleccionarTodosMantenimientos}
                >
                  Seleccionar Todos
                </Button>
              </div>
              <small className="text-muted mt-3 mb-3 d-block">
                * Debe seleccionar al menos un mantenimiento
              </small>
              <div
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  border: "1px solid #dee2e6",
                  padding: "10px",
                  borderRadius: "4px"
                }}
              >
                {mantenimientos.map((m: any) => (
                  <FormGroup check key={m.id} className="mb-1">
                    <Label check>
                      <Input
                        type="checkbox"
                        style={{
                          accentColor: "#dc3545"
                        }}
                        checked={mantenimientosSeleccionados.includes(
                          m.id.toString()
                        )}
                        onChange={e => {
                          if (e.target.checked) {
                            onMantenimientoChange([
                              ...mantenimientosSeleccionados,
                              m.id.toString()
                            ]);
                          } else {
                            onMantenimientoChange(
                              mantenimientosSeleccionados.filter(
                                id => id !== m.id.toString()
                              )
                            );
                          }
                        }}
                      />
                      <small>
                        {m.nombre} - {m.id}
                      </small>
                    </Label>
                  </FormGroup>
                ))}
              </div>
              <small className="text-muted">
                {mantenimientosSeleccionados.length} seleccionados
              </small>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Label>Selección de clubes</Label>
                <Button
                  color="danger"
                  size="sm"
                  onClick={onSeleccionarTodosClubes}
                >
                  Seleccionar Todos
                </Button>
              </div>
              <small className="text-muted mb-3 d-block">
                * Debe seleccionar al menos un club
              </small>
              <div
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  border: "1px solid #dee2e6",
                  padding: "10px",
                  borderRadius: "4px"
                }}
              >
                {clubsDisponibles.map((c: any) => (
                  <FormGroup check key={c.id} className="mb-1">
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={clubesSeleccionados.includes(c.id.toString())}
                        onChange={e => {
                          if (e.target.checked) {
                            onClubChange([
                              ...clubesSeleccionados,
                              c.id.toString()
                            ]);
                          } else {
                            onClubChange(
                              clubesSeleccionados.filter(
                                id => id !== c.id.toString()
                              )
                            );
                          }
                        }}
                      />
                      <small>
                        {c.nombre} - {c.claveCorta}
                      </small>
                    </Label>
                  </FormGroup>
                ))}
              </div>
              <small className="text-muted">
                {clubesSeleccionados.length} seleccionados
              </small>
            </FormGroup>
          </Col>
        </Row>

        <hr />
        <h6>Configurar horarios por día</h6>
        <small className="text-muted mb-3 d-block">
          * Debe configurar al menos un día con horarios válidos
        </small>
        {DIAS_SEMANA.map(dia => {
          const horario = nuevaConfiguracion.horarios.find(
            h => h.dia === dia.key
          );
          return (
            <Row key={dia.key} className="mb-2 align-items-center">
              <Col md={2}>
                <FormGroup check>
                  <Label check>
                    <Input
                      type="checkbox"
                      style={{
                        accentColor: "#dc3545"
                      }}
                      checked={horario ? horario.activo : false}
                      onChange={e => {
                        const nuevosHorarios = nuevaConfiguracion.horarios.map(
                          h =>
                            h.dia === dia.key
                              ? { ...h, activo: e.target.checked }
                              : h
                        );
                        onHorarioChange(nuevosHorarios);
                      }}
                    />
                    {dia.nombre}
                  </Label>
                </FormGroup>
              </Col>
              <Col md={4}>
                <Input
                  type="time"
                  value={horario ? horario.horaInicio : "08:00"}
                  disabled={!(horario ? horario.activo : false)}
                  onChange={e => {
                    const nuevosHorarios = nuevaConfiguracion.horarios.map(h =>
                      h.dia === dia.key
                        ? { ...h, horaInicio: e.target.value }
                        : h
                    );
                    onHorarioChange(nuevosHorarios);
                  }}
                />
              </Col>
              <Col md={4}>
                <Input
                  type="time"
                  value={horario ? horario.horaFin : "18:00"}
                  disabled={!(horario ? horario.activo : false)}
                  onChange={e => {
                    const nuevosHorarios = nuevaConfiguracion.horarios.map(h =>
                      h.dia === dia.key ? { ...h, horaFin: e.target.value } : h
                    );
                    onHorarioChange(nuevosHorarios);
                  }}
                />
              </Col>
            </Row>
          );
        })}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Cancelar
        </Button>
        <Button
          color="danger"
          onClick={onCrear}
          disabled={isAnyOperationInProgress()}
        >
          {loadingStates.saving ? (
            <>
              <i className="fa fa-spinner fa-spin mr-2"></i>
              Creando...
            </>
          ) : (
            "Crear Configuración"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ModalNuevaConfiguracion;
